import { useParams } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { useSharedOrderView } from '../../api/orderApi';
import { copyText } from '../../utils/clipboard';
import { BRAND_CSS_VARS, BRAND_NAME, BRAND_SLOGAN } from '../../lib/brand';

/**
 * 标签页标题：访客在微信/浏览器里同时开好几条链接时，认的是这张单而不是这套系统。
 * 站点名（ICP 备案名）不出现——`App.tsx` 的全局标题效应对 `/share/` 让路，见那里的注释。
 */
const SHARE_PAGE_TITLE = `订单服务确认单 · ${BRAND_NAME}`;

/** 服务端派生状态决定徽标配色，与订单列表页同一套取值 */
const STATUS_COLORS: Record<string, string> = {
  '未执行': 'bg-amber-50 text-amber-700 border-amber-200',
  '执行中': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  '已完成': 'bg-teal-50 text-teal-700 border-teal-200',
  '已取消': 'bg-rose-50 text-rose-600 border-rose-200',
  '已归档': 'bg-gray-100 text-gray-500 border-gray-200',
};

const CARD_CLASS =
  'bg-white rounded-2xl border border-gray-100 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_32px_-16px_rgba(16,24,40,0.16)]';

function Row({ label, value, strong }: { label: string; value: string | null; strong?: boolean }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="text-xs text-gray-500 shrink-0">{label}</span>
      <span
        className={`text-right break-all ${
          strong ? 'text-base font-semibold text-gray-900 leading-6' : 'text-sm text-gray-700'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

/** 分组标题：一条品牌色短标 + 加重的字，把「组」和「行」拉开层级 */
function SectionTitle({ children }: { children: string }) {
  return (
    <p className="flex items-center gap-1.5 mb-2">
      <span className="w-1 h-3 rounded-full bg-[var(--brand)] shrink-0" />
      <span className="text-xs font-semibold text-gray-600 tracking-wide">{children}</span>
    </p>
  );
}

/** 分组容器：分隔线用 gray-100（gray-50 在白卡上几乎看不见，几组会糊成一块） */
function Section({ children }: { children: ReactNode }) {
  return <div className="px-5 py-4 border-b border-gray-100">{children}</div>;
}

/**
 * 页面骨架：品牌色带 + 上叠的白卡。
 *
 * 色带与卡片刻意咬合（卡片 `-mt-16` 压进色带下沿），否则页头就是两行字浮在灰底上、
 * 与下面的卡片各不相干。渐变只走 `to-b`：白字落在深端（≥4.7:1），浅端被卡片盖住。
 * 加载、失效、正常三态共用这一层，切换时页头不跳动。
 */
function PageShell({ children }: { children: ReactNode }) {
  useEffect(() => {
    document.title = SHARE_PAGE_TITLE;
  }, []);

  return (
    <div className="min-h-screen bg-[var(--brand-page)] pb-10" style={BRAND_CSS_VARS}>
      <div className="bg-gradient-to-b from-[var(--brand-deep)] to-[var(--brand-strong)]">
        <div className="max-w-md mx-auto px-4 pt-12 pb-24 text-center">
          <p className="text-lg font-semibold text-white tracking-wide">{BRAND_NAME}</p>
          <p className="text-sm text-white/80 mt-1.5 tracking-wide">{BRAND_SLOGAN}</p>
        </div>
      </div>
      <div className="max-w-md mx-auto px-4 -mt-16">{children}</div>
    </div>
  );
}

function money(n: number): string {
  return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`;
}

/**
 * 订单分享链接的公开访问页：不登录即可打开，凭 URL 里的令牌换取服务端白名单摘要。
 * 页面本身不放任何导航入口，也不展示审核状态、支付凭证等内部字段——能看到的就是摘要那几项。
 * 品牌区纯文字、不带链接：分享页是对外公开页，放入口等于把别人的客户流量引回站点。
 */
export default function SharedOrderPage() {
  const { token } = useParams<{ token: string }>();
  const { data, isPending, error } = useSharedOrderView(token ?? '');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!data) return;
    try {
      await copyText(data.summaryText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      alert(e instanceof Error && e.message ? e.message : '复制失败，请手动选中复制');
    }
  };

  if (isPending) {
    return (
      <PageShell>
        <div className={`${CARD_CLASS} px-5 py-8 text-center`}>
          <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
            正在加载订单摘要…
          </p>
        </div>
      </PageShell>
    );
  }

  if (!data) {
    return (
      <PageShell>
        <div className={`${CARD_CLASS} p-8 text-center`}>
          <div className="w-12 h-12 mx-auto flex items-center justify-center bg-gray-100 rounded-full mb-4">
            <i className="ri-link-m text-gray-400 text-xl" aria-hidden="true" />
          </div>
          <p className="text-sm font-medium text-gray-900 mb-1.5">链接无法打开</p>
          <p className="text-sm text-gray-500 leading-relaxed">
            {error instanceof Error && error.message
              ? error.message
              : '分享链接无效或已过期，请联系分享者重新生成'}
          </p>
        </div>
      </PageShell>
    );
  }

  const s = data.summary;
  const statusCls = STATUS_COLORS[s.orderStatus ?? ''] ?? 'bg-gray-100 text-gray-500 border-gray-200';

  return (
    <PageShell>
      <div className={`${CARD_CLASS} overflow-hidden`}>
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-base font-semibold text-gray-900">订单服务确认单</p>
              <p className="text-xs text-gray-500 font-mono truncate mt-0.5">{s.orderNumber ?? '-'}</p>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full border font-medium shrink-0 whitespace-nowrap ${statusCls}`}>
              {s.orderStatus ?? '-'}
            </span>
          </div>
        </div>

        <Section>
          <SectionTitle>客户信息</SectionTitle>
          <Row label="客户名称" value={s.customerName} />
          <Row label="所属单位" value={s.companyName} />
          <Row label="联系人" value={s.contactPerson} />
          <Row label="联系电话" value={s.contactPhone} />
          <Row label="活动地址" value={s.activityAddress} />
        </Section>

        <Section>
          <SectionTitle>服务时间</SectionTitle>
          <Row label="订单分类" value={s.orderCategory} />
          <Row label="服务类型" value={s.serviceTypeName} />
          <Row label="活动日期" value={s.orderStartDate} />
          <Row label="结束日期" value={s.orderEndDate !== s.orderStartDate ? s.orderEndDate : null} />
          <Row label="服务时间" value={s.serviceTimeText} />
          <Row label="到达时间" value={s.arrivalText} />
        </Section>

        <Section>
          <SectionTitle>费用信息</SectionTitle>
          <Row label="订单总额" value={money(s.orderAmount)} strong />
          <Row label="已收款" value={money(s.paidAmount)} />
          {s.balance > 0 && <Row label="待收余款" value={money(s.balance)} />}
          <Row label="付款状态" value={s.paymentStatusText} />
          <Row label="付款方式" value={s.paymentMethod} />
        </Section>

        {s.specialInstructions && (
          <Section>
            <SectionTitle>特殊说明</SectionTitle>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{s.specialInstructions}</p>
          </Section>
        )}

        <div className="px-5 py-4">
          <button
            onClick={handleCopy}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium cursor-pointer transition-colors whitespace-nowrap bg-[var(--brand-strong)] hover:bg-[var(--brand-deep)] text-white"
          >
            <i className={`${copied ? 'ri-check-line' : 'ri-clipboard-line'} text-sm`} aria-hidden="true" />
            {copied ? '已复制到剪贴板！' : '复制文字摘要'}
          </button>
        </div>
      </div>

      <p className="text-xs text-center text-gray-500 mt-4 leading-relaxed">
        本页面由订单所有者分享，内容以其账户视角下的订单摘要为准
        <br />
        链接于 {new Date(data.expiresAt).toLocaleString('zh-CN')} 后失效
      </p>
    </PageShell>
  );
}
